import { useState, useCallback, type KeyboardEvent } from 'react';
import { fetchComments, createComment, markThreadRead, type Comment, type CommentSummary } from '@/lib/api';
import { toast } from '@/stores/toast.store';
import { useChatStore } from '@/stores/chat.store';

/**
 * A side conversation attached to the message that caused it (design v2).
 *
 * Collapsed it is one line: how many replies, who is in it, and — the part
 * that matters — WHO CAN SEE IT. Expanded it shows the replies and its own
 * composer.
 *
 * The audience is never implied. Comments default to internal, so a reply that
 * the author assumed was going to a vendor could silently never reach them.
 * Both the collapsed row and the composer say who can see it in words, and
 * switching audience is one click. An ambiguous comment composer is a bug, not
 * a polish item.
 */
export function CommentCard({
  areaId,
  messageId,
  summary,
  currentUserId,
  onPosted,
}: {
  areaId: string;
  messageId: string;
  summary: CommentSummary;
  currentUserId?: string;
  onPosted?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // Seeded from the thread being replied to: replying inside an internal
  // thread stays internal unless deliberately changed.
  const [isInternal, setIsInternal] = useState(summary.isInternal);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetchComments(messageId, { limit: 50 });
      setComments(res.comments.filter((c) => c.threadId === summary.threadId));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [messageId, summary.threadId]);

  const toggle = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    if (!next) return;
    // Refetch whenever the cached list can't be trusted: never loaded, or the
    // summary has moved on because comments arrived while this was collapsed.
    // Opening also marks the thread read, so a stale cache would clear the
    // badge for a comment the user was never shown — silently telling them
    // they've read something they haven't.
    if (comments.length === 0 || comments.length !== summary.count || summary.unreadCount > 0) {
      void load();
    }
    // Read means "the user opened it", not "it was delivered". Clear locally
    // first so the badge responds immediately, then persist; a failed write
    // costs a stale badge, never a lost reply.
    if (summary.unreadCount > 0) {
      useChatStore.getState().markCommentThreadRead(areaId, messageId, summary.threadId);
      markThreadRead(summary.threadId).catch(() => {});
    }
  }, [expanded, comments.length, load, areaId, messageId, summary.threadId, summary.unreadCount, summary.count]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await createComment(messageId, text, isInternal);
      // There is one thread per (message, audience). Flipping the audience
      // posts into a DIFFERENT thread, so appending it here unconditionally
      // would show a reply to everyone sitting inside the internal thread —
      // exactly the audience confusion this card exists to prevent.
      if (res.comment.threadId === summary.threadId) {
        setComments((prev) => [...prev, res.comment]);
      } else {
        toast.success(
          isInternal
            ? 'Posted to the internal thread on this message.'
            : 'Posted to the everyone thread on this message.',
        );
      }
      setDraft('');
      onPosted?.();
    } catch {
      toast.error("Couldn't post that comment.");
    } finally {
      setSending(false);
    }
  }, [draft, sending, messageId, isInternal, onPosted, summary.threadId]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Same IME guard as the main composer: a composing Enter is not a send.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };

  const audienceLabel = isInternal ? 'Only your team will see this' : 'Everyone in the room will see this';

  return (
    <div
      className={
        summary.isInternal
          ? 'ml-11 mt-1 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30'
          : 'ml-11 mt-1 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-gray-900/50'
      }
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 text-left"
      >
        <svg className="h-3.5 w-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-4 4z" />
        </svg>
        <span className="text-xs font-semibold">
          {summary.count} {summary.isInternal ? 'internal ' : ''}
          {summary.count === 1 ? 'comment' : 'comments'}
        </span>
        {summary.unreadCount > 0 && (
          <span className="rounded-full bg-blue-600 px-1.5 text-[10px] font-bold text-white">
            {summary.unreadCount}
          </span>
        )}
        <span className="ml-auto text-[11px] text-gray-600 dark:text-gray-300">
          {summary.isInternal ? 'only your team sees this' : 'visible to everyone'}
        </span>
        <svg
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {loading && <p className="text-xs text-gray-600 dark:text-gray-300">Loading…</p>}

          {loadError && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-600 dark:text-gray-300">Couldn&apos;t load comments.</span>
              <button type="button" onClick={load} className="font-semibold text-blue-600 hover:underline">
                Try again
              </button>
            </div>
          )}

          {comments.map((c) => (
            <div key={c.id} className="flex items-start gap-2">
              <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-[9px] font-semibold text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">
                {c.author.displayName.charAt(0).toUpperCase()}
              </div>
              <p className="text-[13px] leading-snug">
                <span className="font-semibold">{c.author.displayName}</span>{' '}
                <span className={c.author.userId === currentUserId ? '' : ''}>{c.content}</span>
              </p>
            </div>
          ))}

          <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder={isInternal ? 'Reply internally…' : 'Reply to everyone…'}
              className="w-full resize-none bg-transparent px-3 py-2 text-[13px] outline-none"
            />
            <div className="flex items-center gap-2 border-t border-gray-200 px-2 py-1.5 dark:border-gray-800">
              {/* Audience is stated, not implied — and always one click away. */}
              <button
                type="button"
                onClick={() => setIsInternal((v) => !v)}
                className={
                  isInternal
                    ? 'rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                    : 'rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                }
                title="Switch audience"
              >
                {isInternal ? 'Internal' : 'Everyone'}
              </button>
              <span className="text-[11px] text-gray-600 dark:text-gray-300">{audienceLabel}</span>
              <button
                type="button"
                onClick={send}
                disabled={!draft.trim() || sending}
                className="ml-auto rounded-full bg-blue-600 px-3 py-1 text-[11px] font-semibold text-white disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
